import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPodResponse } from '../interfaces/ICommunityHub';
import { RootState } from './store';

interface PodsState {
  pods: IPodResponse[];
  activePodId: string | null;
  loading: boolean;
  error: string | null;
  podUnreadCounts: { [podId: string]: number };
}

const initialState: PodsState = {
  pods: [],
  activePodId: null,
  loading: false,
  error: null,
  podUnreadCounts: {},
};

const podsSlice = createSlice({
  name: 'pods',
  initialState,
  reducers: {
    setPods(state, action: PayloadAction<IPodResponse[]>) {
      state.pods = action.payload;
    },
    setActivePod(state, action: PayloadAction<string | null>) {
      state.activePodId = action.payload;
    },
    addPod(state, action: PayloadAction<IPodResponse>) {
      state.pods.push(action.payload);
    },
    updatePod(state, action: PayloadAction<IPodResponse>) {
      const index = state.pods.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.pods[index] = action.payload;
      }
    },
    removePod(state, action: PayloadAction<string>) {
      state.pods = state.pods.filter(p => p.id !== action.payload);
      if (state.activePodId === action.payload) {
        state.activePodId = null;
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setPodUnreadCounts(state, action: PayloadAction<{ [podId: string]: number }>) {
      state.podUnreadCounts = action.payload;
    },
    incrementPodUnreadCount(state, action: PayloadAction<string>) {
      state.podUnreadCounts[action.payload] = (state.podUnreadCounts[action.payload] || 0) + 1;
    },
    clearPodUnreadCount(state, action: PayloadAction<string>) {
      state.podUnreadCounts[action.payload] = 0;
    },
  },
});

export const {
  setPods,
  setActivePod,
  addPod,
  updatePod,
  removePod,
  setLoading,
  setError,
  setPodUnreadCounts,
  incrementPodUnreadCount,
  clearPodUnreadCount,
} = podsSlice.actions;

// Selectors
export const selectPods = (state: RootState) => state.pods.pods;
export const selectActivePodId = (state: RootState) => state.pods.activePodId;
export const selectActivePod = (state: RootState) => {
  const activePodId = state.pods.activePodId;
  return activePodId ? state.pods.pods.find(p => p.id === activePodId) : null;
};
export const selectPodsLoading = (state: RootState) => state.pods.loading;
export const selectPodsError = (state: RootState) => state.pods.error;
export const selectPodById = (state: RootState, podId: string) =>
  state.pods.pods.find(p => p.id === podId) ?? null;
export const selectPodUnreadCounts = (state: RootState) => state.pods.podUnreadCounts;
export const selectPodUnreadCount = (state: RootState, podId: string) =>
  state.pods.podUnreadCounts[podId] ?? 0;

export default podsSlice.reducer;
